import DOMPurify from "dompurify";
import { marked } from "marked";
import { type FlavourId, flavourOptions } from "./flavours";

// Markdown carries raw HTML by design, and a document that arrived in the fragment is one somebody else wrote, so
// what marked returns is sanitised before it reaches the DOM: a shared link could otherwise run a script on this
// origin. Synchronous because the preview is a render and not an effect.
export function renderMarkdown(text: string, flavour: FlavourId): string {
  return DOMPurify.sanitize(marked.parse(text, { ...flavourOptions(flavour), async: false }), CONTAINED);
}

// The preview is injected into the page rather than framed, so anything that reaches past its own box reaches the
// site: a stylesheet restyles every page, an inline style can pin a box over all of it, and a control can submit,
// take a password manager's fill or name an element outside the preview by id. The text inside all of them stays.
const CONTAINED = {
  FORBID_TAGS: ["style", "form", "button", "select", "option", "optgroup", "datalist", "textarea", "label", "output"],
  FORBID_ATTR: ["style"],
};

// A link in the preview leads off a page somebody is still writing on, so it opens beside it rather than over it.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.nodeName !== "A" || !node.hasAttribute("href")) return;
  node.setAttribute("target", "_blank");
  node.setAttribute("rel", "noopener noreferrer");
});

// A task list's box is the one input a document is written with, and it is a disabled checkbox; any other is a control.
DOMPurify.addHook("uponSanitizeElement", (node, data) => {
  if (data.tagName !== "input") return;
  const box = node as HTMLInputElement;
  if (box.type !== "checkbox" || !box.disabled) box.remove();
});
